import { formatReportDate, formatReportDateRange, formatReportDateShort } from './cropCalendarReport';

export type FieldHealthClimateSeries = {
  success?: boolean;
  parameter_name?: string;
  daily_data?: Array<{ date: string; value: number }>;
};

export type FieldHealthReportInput = {
  fieldInfo: { farm_name?: string; farm_id?: string | number };
  results?: {
    mean_ndvi?: Array<{ date: string; mean_ndvi: number; cloud_percentage?: number }>;
    mean_evi?: Array<{ date: string; mean_evi: number; cloud_percentage?: number }>;
    mean_msavi2?: Array<{ date: string; mean_msavi2: number; cloud_percentage?: number }>;
    [key: string]: Array<{ date: string; [key: string]: string | number | undefined }> | undefined;
  };
  area_ha?: number;
  crop_name?: string;
  monitoring_period?: string;
  indices_returned?: string[];
  selectedIndices?: string[];
  analysisDate: string;
  reportType: string;
  startDate: string;
  endDate: string;
  weather?: {
    temperature_2m?: FieldHealthClimateSeries | { success: false };
    total_precipitation_sum?: FieldHealthClimateSeries | { success: false };
  } | null;
  recommendations_and_insights?: Record<
    string,
    {
      summary?: string;
      trend?: string;
      trend_description?: string;
      statistics?: {
        average?: number;
        minimum?: number;
        maximum?: number;
        latest?: number;
        latest_date?: string;
        observations_count?: number;
      };
      latest_recommendation?: {
        status?: string;
        value?: number;
        interpretation?: string;
        recommendation?: string;
        category?: string;
      };
    }
  >;
  timeSeriesData?: Record<string, { results?: Array<{ date: string; mean_index_value: number }> }>;
};

export type SeriesPoint = { date: string; value: number };

export type TrendView = { cls: 'up' | 'steady' | 'down'; label: string; arrow: string };

export type IndexSeriesView = {
  key: string;
  label: string;
  subtitle: string;
  color: string;
  points: SeriesPoint[];
  outliers: string[];
  stats: { mean: number; max: number; min: number; latest: number; latestDate: string };
  trend: TrendView;
  latestDateLabel: string;
  interpretation?: string;
  recommendation?: string;
  outlierDateLabels: string[];
};

export type WeatherSeriesView = {
  points: SeriesPoint[];
  stats: { mean: number; max: number; min: number; latest: number; latestDate: string };
  total?: number;
  wetDays?: number;
  wettest?: { value: number; dateLabel: string };
};

export type FieldHealthViewModel = {
  farmName: string;
  farmId: string;
  areaLabel: string;
  cropLabel: string;
  preparedDate: string;
  preparedTime: string;
  periodRange: string;
  dayCount: number;
  obsCount: number;
  measuresLabel: string;
  verdictHeadline: string;
  verdictSummary: string;
  indices: IndexSeriesView[];
  cloudOutlierCount: number;
  weatherDayCount: number;
  temperature?: WeatherSeriesView;
  rainfall?: WeatherSeriesView;
};

export const INDEX_META: Record<string, { label: string; subtitle: string; color: string }> = {
  ndvi: { label: 'Crop greenness', subtitle: 'How green and healthy your crop is.', color: '#008540' },
  evi: { label: 'Greenness in dense crop', subtitle: 'Greenness that stays accurate when the crop is thick.', color: '#1d5fa8' },
  msavi2: { label: 'Greenness on bare soil', subtitle: 'Greenness that ignores bare soil showing through.', color: '#b7852b' },
  savi: { label: 'Soil-adjusted greenness', subtitle: 'Greenness with less influence from bare soil.', color: '#008540' },
};

export function formatFieldHealthValue(v: number, digits = 3): string {
  if (!Number.isFinite(v)) return '—';
  return v.toFixed(digits);
}

export function indexMeta(key: string) {
  const k = key.toLowerCase();
  return (
    INDEX_META[k] || {
      label: key.toUpperCase(),
      subtitle: 'Satellite greenness measure for your field.',
      color: '#008540',
    }
  );
}

export function reportIndices(report: FieldHealthReportInput): string[] {
  return (report.indices_returned || report.selectedIndices || ['ndvi']).map((i) => i.toLowerCase());
}

export function extractSeries(report: FieldHealthReportInput, indexKey: string): SeriesPoint[] {
  const k = indexKey.toLowerCase();
  const resultKey = `mean_${k}`;

  if (report.results?.[resultKey]) {
    const rows = report.results[resultKey];
    if (!Array.isArray(rows)) return [];
    return rows
      .map((item) => ({
        date: item.date,
        value: Number(item[resultKey as keyof typeof item] ?? 0),
      }))
      .filter((r) => Number.isFinite(r.value));
  }

  const legacy = report.timeSeriesData?.[k]?.results;
  if (!legacy?.length) return [];
  return legacy.map((item) => ({
    date: item.date,
    value: Number(item.mean_index_value),
  }));
}

export function detectOutliers(points: SeriesPoint[]): Set<string> {
  const outliers = new Set<string>();
  if (points.length < 3) return outliers;
  for (let i = 0; i < points.length; i += 1) {
    const curr = points[i].value;
    const neighbors: number[] = [];
    if (i > 0) neighbors.push(points[i - 1].value);
    if (i < points.length - 1) neighbors.push(points[i + 1].value);
    if (!neighbors.length) continue;
    const neighborAvg = neighbors.reduce((a, b) => a + b, 0) / neighbors.length;
    if (neighborAvg > 0.05 && curr < neighborAvg * 0.65) outliers.add(points[i].date);
  }
  return outliers;
}

export function clearPoints(points: SeriesPoint[], outliers: Set<string>): SeriesPoint[] {
  return points.filter((p) => !outliers.has(p.date));
}

export function calcStats(points: SeriesPoint[]) {
  if (!points.length) return { mean: 0, max: 0, min: 0, latest: 0, latestDate: '' };
  const values = points.map((p) => p.value);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  return {
    mean,
    max: Math.max(...values),
    min: Math.min(...values),
    latest: values[values.length - 1],
    latestDate: points[points.length - 1].date,
  };
}

export function trendInfo(trend?: string, trendDescription?: string): TrendView {
  const t = (trend || '').toLowerCase();
  if (t === 'improving' || t === 'increasing') {
    return { cls: 'up', label: 'Improving', arrow: '↗' };
  }
  if (t === 'declining' || t === 'decreasing') {
    return { cls: 'down', label: 'Easing', arrow: '↘' };
  }
  const desc = (trendDescription || '').toLowerCase();
  if (desc.includes('improv') || desc.includes('increas')) {
    return { cls: 'up', label: 'Improving', arrow: '↗' };
  }
  if (desc.includes('declin') || desc.includes('decreas')) {
    return { cls: 'down', label: 'Easing', arrow: '↘' };
  }
  return { cls: 'steady', label: 'Steady', arrow: '→' };
}

export function trendLabel(trend: TrendView): string {
  return trend.label;
}

/** Cloud-affected readings can be invalid (e.g. negative EVI) — don't show scary numbers. */
export function formatCloudOutlierValue(value: number): string {
  if (!Number.isFinite(value) || value < -0.5 || value > 5) return 'Unusual';
  return formatFieldHealthValue(value);
}

export function verdictHeadline(category?: string): string {
  const c = (category || '').toLowerCase();
  if (c === 'high') return 'Your crop looks healthy';
  if (c === 'medium') return 'Your crop is doing okay';
  if (c === 'low') return 'Your crop needs attention';
  return 'Your field at a glance';
}

export function buildVerdictSummary(report: FieldHealthReportInput, primaryKey: string): string {
  const insights = report.recommendations_and_insights?.[primaryKey];
  const series = extractSeries(report, primaryKey);
  const outliers = detectOutliers(series);
  const clear = clearPoints(series, outliers);
  const stats = calcStats(clear.length ? clear : series);
  const meta = indexMeta(primaryKey);
  const latestDate = formatReportDate(stats.latestDate || insights?.statistics?.latest_date);
  const trend = trendInfo(insights?.trend, insights?.trend_description);
  const trendWord = trend.cls === 'up' ? 'improving' : trend.cls === 'down' ? 'easing' : 'holding steady';
  return `${meta.label} reads ${formatFieldHealthValue(stats.latest)} on ${latestDate}, and has been ${trendWord} across the period.`;
}

export function daysBetween(start: string, end: string): number {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  return Math.max(1, Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)));
}

export function isClimateOk(
  w: unknown
): w is FieldHealthClimateSeries & { daily_data: Array<{ date: string; value: number }> } {
  if (!w || typeof w !== 'object') return false;
  const o = w as FieldHealthClimateSeries;
  return o.success !== false && Array.isArray(o.daily_data) && o.daily_data.length > 0;
}

export function chartYTicks(min: number, max: number, count = 4): number[] {
  const range = max - min || 1;
  return Array.from({ length: count }, (_, i) => min + (range * i) / (count - 1));
}

function buildIndexView(report: FieldHealthReportInput, indexKey: string): IndexSeriesView | null {
  const meta = indexMeta(indexKey);
  const points = extractSeries(report, indexKey);
  if (!points.length) return null;
  const outlierSet = detectOutliers(points);
  const clear = clearPoints(points, outlierSet);
  const stats = calcStats(clear.length ? clear : points);
  const insights = report.recommendations_and_insights?.[indexKey];
  const rec = insights?.latest_recommendation;
  return {
    key: indexKey,
    label: meta.label,
    subtitle: meta.subtitle,
    color: meta.color,
    points,
    outliers: [...outlierSet],
    stats,
    trend: trendInfo(insights?.trend, insights?.trend_description),
    latestDateLabel: formatReportDate(stats.latestDate),
    interpretation: rec?.interpretation,
    recommendation: rec?.recommendation,
    outlierDateLabels: points.filter((p) => outlierSet.has(p.date)).map((p) => formatReportDateShort(p.date)),
  };
}

export function buildFieldHealthViewModel(report: FieldHealthReportInput): FieldHealthViewModel {
  const indices = reportIndices(report);
  const farmName = report.fieldInfo?.farm_name || 'Field';
  const farmId = String(report.fieldInfo?.farm_id ?? '');
  const prepared = new Date(report.analysisDate);
  const preparedDate = prepared.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'Asia/Manila',
  });
  const preparedTime = prepared.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Manila',
  });
  const periodRange = formatReportDateRange(report.startDate, report.endDate);
  const dayCount = daysBetween(report.startDate, report.endDate);
  const obsCount = Math.max(...indices.map((k) => extractSeries(report, k).length), 0);
  const primaryKey = indices.includes('ndvi') ? 'ndvi' : indices[0];
  const primaryInsights = report.recommendations_and_insights?.[primaryKey];

  let cloudOutlierCount = 0;
  for (const key of indices) cloudOutlierCount += detectOutliers(extractSeries(report, key)).size;

  const indexViews = indices.map((k) => buildIndexView(report, k)).filter(Boolean) as IndexSeriesView[];

  const temp = report.weather?.temperature_2m;
  const rain = report.weather?.total_precipitation_sum;
  const tempOk = isClimateOk(temp);
  const rainOk = isClimateOk(rain);
  const tempPoints = tempOk ? temp.daily_data.map((d) => ({ date: d.date, value: d.value })) : [];
  const rainPoints = rainOk ? rain.daily_data.map((d) => ({ date: d.date, value: d.value })) : [];

  let temperature: WeatherSeriesView | undefined;
  if (tempOk && tempPoints.length >= 2) {
    temperature = { points: tempPoints, stats: calcStats(tempPoints) };
  }

  let rainfall: WeatherSeriesView | undefined;
  if (rainOk && rainPoints.length >= 1) {
    const stats = calcStats(rainPoints);
    const total = rainPoints.reduce((a, p) => a + p.value, 0);
    const wettest = rainPoints.reduce((best, p) => (p.value > best.value ? p : best), rainPoints[0]);
    rainfall = {
      points: rainPoints,
      stats,
      total,
      wetDays: rainPoints.filter((p) => p.value > 10).length,
      wettest: { value: wettest.value, dateLabel: formatReportDateShort(wettest.date) },
    };
  }

  return {
    farmName,
    farmId,
    areaLabel: report.area_ha != null ? `${report.area_ha.toFixed(2)} ha` : '—',
    cropLabel: report.crop_name?.trim() || 'Not set',
    preparedDate,
    preparedTime,
    periodRange,
    dayCount,
    obsCount,
    measuresLabel: indices.map((i) => i.toUpperCase()).join(', '),
    verdictHeadline: verdictHeadline(primaryInsights?.latest_recommendation?.category),
    verdictSummary: buildVerdictSummary(report, primaryKey),
    indices: indexViews,
    cloudOutlierCount,
    weatherDayCount: Math.max(tempPoints.length, rainPoints.length),
    temperature,
    rainfall,
  };
}
